import { Table, InputNumber } from "antd";
import Column from "antd/es/table/Column";
import React from "react";
import { formatVND, layTenNhomNhanSu } from "../../helpers/common";
import { ChiTietLuongPhuCapItem } from "../../models/luong/LuongPhuCapModel";

interface Props {
    data: Array<ChiTietLuongPhuCapItem>;
    loading?: boolean;
    isEdit?: boolean;
    isDieuChinh?: boolean;
    onChangeItem?: (index: number, field: string, newValue: number) => void;
}

const romanNumerals = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];

export interface FlatRowLuongPhuCap {
    key: string;
    type: 'grand-summary' | 'group-summary' | 'employee';
    groupIndex?: string;
    employeeIndex?: number;
    originalIndex: number;
    id?: number;
    ma?: string;
    hoTen: string | React.ReactNode;
    tenChucVu?: string;
    tenDonVi?: string;
    soNgayCongAnCa?: number;
    soNgayCongCTP?: number;
    mucChiTienAnCa?: number;
    thanhTienAnCa?: number;
    mucKhoanDienThoai?: number;
    mucKhoanCongTac?: number;
    tongCongDuocNhan?: number;
    soTienDaUng?: number;
    soTienConDuocNhan?: number;
    ghiChu?: string;
}

const buildFlatRows = (items: ChiTietLuongPhuCapItem[]): FlatRowLuongPhuCap[] => {
    const flatRows: FlatRowLuongPhuCap[] = [];
    if (!items || items.length === 0) return flatRows;

    // Group items by idNhom (fallback: item.isQuanLy ? 3 : 1)
    const groups: { [key: number]: { originalIndex: number; item: ChiTietLuongPhuCapItem }[] } = {};
    items.forEach((item, index) => {
        const nhomId = item.idNhom || (item.isQuanLy ? 3 : 1);
        if (!groups[nhomId]) {
            groups[nhomId] = [];
        }
        groups[nhomId].push({ originalIndex: index, item });
    });

    const preferredOrder = [3, 7, 1];
    const allGroupIds = Object.keys(groups).map(Number);
    const sortedGroupIds: number[] = [
        ...preferredOrder.filter(id => allGroupIds.includes(id)),
        ...allGroupIds.filter(id => !preferredOrder.includes(id))
    ];

    // Compute Grand Totals
    let grandThanhTienAnCa = 0;
    let grandDienThoai = 0;
    let grandCongTac = 0;
    let grandTongDuocNhan = 0;
    let grandDaUng = 0;
    let grandConNhan = 0;

    items.forEach(item => {
        grandThanhTienAnCa += item.thanhTienAnCa || 0;
        grandDienThoai += item.mucKhoanDienThoai || 0;
        grandCongTac += item.mucKhoanCongTac || 0;
        grandTongDuocNhan += item.tongCongDuocNhan || 0;
        grandDaUng += item.soTienDaUng || 0;
        grandConNhan += item.soTienConDuocNhan || 0;
    });

    // Add Grand Summary row at top
    flatRows.push({
        key: 'grand-summary',
        type: 'grand-summary',
        originalIndex: -1,
        hoTen: 'Tổng cộng',
        thanhTienAnCa: grandThanhTienAnCa,
        mucKhoanDienThoai: grandDienThoai,
        mucKhoanCongTac: grandCongTac,
        tongCongDuocNhan: grandTongDuocNhan,
        soTienDaUng: grandDaUng,
        soTienConDuocNhan: grandConNhan,
    });

    let employeeSTT = 1;

    sortedGroupIds.forEach((nhomId, groupIdx) => {
        const groupItems = groups[nhomId];
        const groupName = nhomId === 7 ? "Trưởng Ban kiểm soát" : layTenNhomNhanSu(nhomId);
        const groupRoman = romanNumerals[groupIdx] || (groupIdx + 1).toString();

        let groupThanhTienAnCa = 0;
        let groupDienThoai = 0;
        let groupCongTac = 0;
        let groupTongDuocNhan = 0;
        let groupDaUng = 0;
        let groupConNhan = 0;

        groupItems.forEach(({ item }) => {
            groupThanhTienAnCa += item.thanhTienAnCa || 0;
            groupDienThoai += item.mucKhoanDienThoai || 0;
            groupCongTac += item.mucKhoanCongTac || 0;
            groupTongDuocNhan += item.tongCongDuocNhan || 0;
            groupDaUng += item.soTienDaUng || 0;
            groupConNhan += item.soTienConDuocNhan || 0;
        });

        // Add Group Summary Row
        flatRows.push({
            key: `group-summary-${nhomId}`,
            type: 'group-summary',
            originalIndex: -1,
            groupIndex: groupRoman,
            hoTen: groupName,
            thanhTienAnCa: groupThanhTienAnCa,
            mucKhoanDienThoai: groupDienThoai,
            mucKhoanCongTac: groupCongTac,
            tongCongDuocNhan: groupTongDuocNhan,
            soTienDaUng: groupDaUng,
            soTienConDuocNhan: groupConNhan,
        });

        // Add Group Employee Rows
        groupItems.forEach(({ originalIndex, item }) => {
            flatRows.push({
                key: `employee-${item.idNhanVien || item.id}-${originalIndex}`,
                type: 'employee',
                employeeIndex: employeeSTT++,
                originalIndex,
                id: item.id,
                ma: item.ma,
                hoTen: item.hoTen,
                tenChucVu: item.tenChucVu,
                tenDonVi: item.tenDonVi,
                soNgayCongAnCa: item.soNgayCongAnCa,
                soNgayCongCTP: item.soNgayCongCTP,
                mucChiTienAnCa: item.mucChiTienAnCa,
                thanhTienAnCa: item.thanhTienAnCa,
                mucKhoanDienThoai: item.mucKhoanDienThoai,
                mucKhoanCongTac: item.mucKhoanCongTac,
                tongCongDuocNhan: item.tongCongDuocNhan,
                soTienDaUng: item.soTienDaUng,
                soTienConDuocNhan: item.soTienConDuocNhan,
                ghiChu: item.ghiChu,
            });
        });
    });

    return flatRows;
};

const TableChiTietLuongPhuCap = ({ data, loading, isEdit, isDieuChinh, onChangeItem }: Props) => {
    const flatRows = buildFlatRows(data);

    // isMoney = false dùng cho các cột số công (không phải tiền), không format theo VND
    const renderCell = (value: number | undefined, record: FlatRowLuongPhuCap, field: keyof ChiTietLuongPhuCapItem, isMoney: boolean = true) => {
        if (record.type !== 'employee') {
            if (value === undefined || value === null) return null;
            return isMoney ? formatVND(value) : value;
        }

        if (isEdit) {
            return (
                <InputNumber
                    min={0}
                    value={value}
                    formatter={isMoney ? (val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : (val) => `${val}`}
                    parser={isMoney ? (val) => val!.replace(/\$\s?|(,*)/g, '') : (val) => val!.replace(/[^\d]/g, '')}
                    onChange={(val) => onChangeItem && onChangeItem(record.originalIndex, field, Number(val) || 0)}
                    style={{ width: '100%' }}
                />
            );
        }
        return isMoney ? formatVND(value || 0) : (value ?? 0);
    };

    return (
        <>
            <style>{`
                .table-row-grand-summary td {
                    background-color: #fff9e6 !important;
                    font-weight: bold;
                }
                .table-row-group-summary td {
                    background-color: #e6f7ff !important;
                    font-weight: bold;
                }
            `}</style>
            <Table
                sticky
                dataSource={flatRows}
                loading={loading}
                rowKey="key"
                pagination={false}
                scroll={{ x: 'max-content' }}
                bordered
                rowClassName={(record: FlatRowLuongPhuCap) => {
                    if (record.type === 'grand-summary') return 'table-row-grand-summary';
                    if (record.type === 'group-summary') return 'table-row-group-summary';
                    return '';
                }}
            >
                <Column
                    title="STT"
                    width={50}
                    fixed="left"
                    render={(value, record: FlatRowLuongPhuCap) => {
                        if (record.type === 'grand-summary') return null;
                        if (record.type === 'group-summary') return <strong>{record.groupIndex}</strong>;
                        return record.employeeIndex;
                    }}
                />
                <Column
                    title="Mã NV"
                    dataIndex="ma"
                    fixed="left"
                    width={100}
                    render={(value, record: FlatRowLuongPhuCap) => record.type === 'employee' ? value : null}
                />
                <Column
                    title="Họ và tên"
                    dataIndex="hoTen"
                    fixed="left"
                    width={200}
                    render={(value, record: FlatRowLuongPhuCap) => {
                        if (record.type === 'grand-summary') return <strong>Tổng cộng</strong>;
                        if (record.type === 'group-summary') return <strong>{value}</strong>;
                        return value;
                    }}
                />
                <Column
                    title="Chức vụ"
                    dataIndex="tenChucVu"
                    width={150}
                    render={(value, record: FlatRowLuongPhuCap) => record.type === 'employee' ? value : null}
                />
                <Column
                    title="Đơn vị"
                    dataIndex="tenDonVi"
                    width={200}
                    render={(value, record: FlatRowLuongPhuCap) => record.type === 'employee' ? value : null}
                />
                <Column
                    title="Ngày công ăn ca"
                    dataIndex="soNgayCongAnCa"
                    width={150}
                    align="right"
                    render={(value, record: FlatRowLuongPhuCap) => renderCell(value, record, 'soNgayCongAnCa', false)}
                />
                <Column
                    title="Ngày công CTP"
                    dataIndex="soNgayCongCTP"
                    width={150}
                    align="right"
                    render={(value, record: FlatRowLuongPhuCap) => renderCell(value, record, 'soNgayCongCTP', false)}
                />
                <Column
                    title="Mức chi tiền ăn ca"
                    dataIndex="mucChiTienAnCa"
                    width={150}
                    align="right"
                    render={(value, record: FlatRowLuongPhuCap) => record.type === 'employee' ? formatVND(value || 0) : null}
                />
                <Column
                    title="Thành tiền ăn ca"
                    dataIndex="thanhTienAnCa"
                    width={150}
                    align="right"
                    render={(value, record: FlatRowLuongPhuCap) => formatVND(value || 0)}
                />
                <Column
                    title="Mức khoán điện thoại"
                    dataIndex="mucKhoanDienThoai"
                    width={180}
                    align="right"
                    render={(value, record: FlatRowLuongPhuCap) => renderCell(value, record, 'mucKhoanDienThoai', true)}
                />
                <Column
                    title="Mức khoán công tác"
                    dataIndex="mucKhoanCongTac"
                    width={180}
                    align="right"
                    render={(value, record: FlatRowLuongPhuCap) => renderCell(value, record, 'mucKhoanCongTac', true)}
                />
                <Column
                    title="Tổng cộng được nhận"
                    dataIndex="tongCongDuocNhan"
                    width={180}
                    align="right"
                    render={(value, record: FlatRowLuongPhuCap) => formatVND(value || 0)}
                />
                {isDieuChinh && (
                    <Column
                        title="Số tiền đã tạm ứng"
                        dataIndex="soTienDaUng"
                        width={150}
                        align="right"
                        render={(value, record: FlatRowLuongPhuCap) => formatVND(value || 0)}
                    />
                )}
                <Column
                    title="Số tiền còn được nhận"
                    dataIndex="soTienConDuocNhan"
                    width={180}
                    align="right"
                    render={(value, record: FlatRowLuongPhuCap) => formatVND(value || 0)}
                />
                <Column
                    title="Ghi chú"
                    dataIndex="ghiChu"
                    width={200}
                    render={(value, record: FlatRowLuongPhuCap) => record.type === 'employee' ? value : null}
                />
            </Table>
        </>
    );
};

export default TableChiTietLuongPhuCap;